import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

export default function AddMenuItemForm({
  // form state
  itemName, onItemNameChange,
  itemDescription, onItemDescriptionChange,
  itemPrice, onItemPriceChange,
  itemSection, onItemSectionChange,
  itemCategory, onItemCategoryChange,
  imagePreview, onImageChange,
  // translation state
  supportedLanguages, baseLang, onBaseLangChange,
  activeLangTab, onActiveLangTabChange,
  translations, onTranslationChange,
  translating, onAutoTranslate,
  // section options
  sectionOptions,
  // template loader
  showTemplateCard,
  menuTemplates,
  selectedTemplateToApply,
  onSelectTemplate,
  applyingTemplate,
  onApplyTemplate,
  // bulk translate
  onOpenBulkTranslate,
  // submit/cancel
  onSubmit,
  onCancel,
}) {
  const { t } = useTranslation();

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h3>➕ {t('admin.addMenuItem.title')}</h3>
        <button type="button" className="btn btn-secondary" onClick={onOpenBulkTranslate}>
          🌐 {t('translation.bulkTranslate')}
        </button>
      </div>
      <div className="admin-card-body">
        {showTemplateCard && (
          <div className="template-onboarding-card">
            <div className="template-icon">🚀</div>
            <h3>{t('admin.loadFromTemplate.title')}</h3>
            <p>{t('admin.loadFromTemplate.hint')}</p>
            <div className="template-select-row">
              <select
                className="form-input"
                value={selectedTemplateToApply}
                onChange={(e) => onSelectTemplate(e.target.value)}
                style={{ minWidth: '200px' }}
              >
                <option value="">{t('admin.loadFromTemplate.selectTemplate')}</option>
                {menuTemplates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name} ({tpl.itemCount || tpl.items?.length || 0} {t('common.items')})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onApplyTemplate}
                disabled={!selectedTemplateToApply || applyingTemplate}
              >
                {applyingTemplate ? '…' : t('admin.loadFromTemplate.applyButton')}
              </button>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit}>
          <div className="form-section">
            <div className="form-section-title">📝 {t('admin.addMenuItem.itemDetails', 'Item Details')}</div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>{t('translation.baseLanguage')}</label>
              <select
                className="form-input"
                value={baseLang}
                onChange={(e) => {
                  onBaseLangChange(e.target.value);
                  onActiveLangTabChange(e.target.value);
                }}
                style={{ maxWidth: '250px' }}
              >
                {supportedLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.nativeName} ({lang.name})
                  </option>
                ))}
              </select>
              <small style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {t('translation.baseLanguageHint')}
              </small>
            </div>

            <div className="language-tabs-header">
              {supportedLanguages.map((lang) => {
                const trans = translations[lang.code];
                const isEmpty = !trans.name;
                const isBase = lang.code === baseLang;
                const isAuto = trans.isAutoTranslated;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    className={`language-tab-btn ${activeLangTab === lang.code ? 'active' : ''}`}
                    onClick={() => onActiveLangTabChange(lang.code)}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.nativeName}</span>
                    {isBase && <span className="lang-status" title={t('translation.baseLanguage')}>📝</span>}
                    {!isBase && isAuto && !isEmpty && <span className="lang-status" title={t('translation.autoTranslated')}>🤖</span>}
                    {!isBase && !isAuto && !isEmpty && <span className="lang-status" title={t('translation.manuallyEdited')}>✓</span>}
                    {isEmpty && !isBase && <span className="lang-status" style={{ opacity: 0.5 }}>—</span>}
                  </button>
                );
              })}
            </div>

            {supportedLanguages.map((lang) => (
              <div
                key={lang.code}
                style={{
                  display: activeLangTab === lang.code ? 'block' : 'none',
                  direction: lang.rtl ? 'rtl' : 'ltr',
                }}
              >
                <div className="form-group">
                  <label>{t('admin.addMenuItem.itemName')} ({lang.nativeName})</label>
                  <input
                    type="text"
                    className="form-input"
                    value={lang.code === baseLang ? itemName : translations[lang.code].name}
                    onChange={(e) => {
                      if (lang.code === baseLang) {
                        onItemNameChange(e.target.value);
                      } else {
                        onTranslationChange(lang.code, 'name', e.target.value);
                      }
                    }}
                    placeholder={t('admin.addMenuItem.itemNamePlaceholder')}
                    style={{ textAlign: lang.rtl ? 'right' : 'left' }}
                  />
                </div>

                <div className="form-group">
                  <label>{t('admin.addMenuItem.description')} ({lang.nativeName})</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={lang.code === baseLang ? itemDescription : translations[lang.code].description}
                    onChange={(e) => {
                      if (lang.code === baseLang) {
                        onItemDescriptionChange(e.target.value);
                      } else {
                        onTranslationChange(lang.code, 'description', e.target.value);
                      }
                    }}
                    placeholder={t('admin.addMenuItem.descriptionPlaceholder')}
                    style={{ textAlign: lang.rtl ? 'right' : 'left', resize: 'vertical' }}
                  />
                </div>

                {translations[lang.code].isAutoTranslated && lang.code !== baseLang && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem', background: '#f0f9ff', borderRadius: '4px',
                    fontSize: '0.875rem', color: '#0369a1', marginBottom: '1rem',
                  }}>
                    <span>🤖</span>
                    <span>{t('translation.autoTranslatedNote')}</span>
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              className="btn btn-primary"
              onClick={onAutoTranslate}
              disabled={translating || !itemName}
            >
              {translating ? (
                <>
                  <CircularProgress size={18} color="inherit" />
                  <span>{t('translation.translating')}</span>
                </>
              ) : (
                <>
                  <span>🔄</span>
                  <span>{t('translation.autoTranslateOthers')}</span>
                </>
              )}
            </button>
          </div>

          <div className="form-section">
            <div className="form-section-title">💰 {t('admin.addMenuItem.pricingCategory', 'Pricing & Category')}</div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.addMenuItem.price')}</label>
                <input
                  type="number"
                  className="form-input"
                  value={itemPrice}
                  onChange={(e) => onItemPriceChange(e.target.value)}
                  placeholder={t('admin.addMenuItem.pricePlaceholder')}
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.addMenuItem.sectionLabel')}</label>
                <select
                  className="form-input"
                  value={itemSection}
                  onChange={(e) => onItemSectionChange(e.target.value)}
                  required
                >
                  <option value="">{t('admin.addMenuItem.sectionPlaceholder')}</option>
                  {sectionOptions.map((section) => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </select>
                <small style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{t('admin.addMenuItem.sectionHint')}</small>
              </div>
              <div className="form-group">
                <label>{t('admin.addMenuItem.categoryLabel')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={itemCategory}
                  onChange={(e) => onItemCategoryChange(e.target.value)}
                  placeholder={t('admin.addMenuItem.categoryPlaceholder')}
                />
                <small style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{t('admin.addMenuItem.categoryHint')}</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">🖼️ {t('admin.addMenuItem.image', 'Image')}</div>
            <div
              className={`file-upload-area ${imagePreview ? 'has-file' : ''}`}
              onClick={() => document.getElementById('item-image').click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && document.getElementById('item-image').click()}
            >
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <div>{t('admin.addMenuItem.clickToChange')}</div>
                </div>
              ) : (
                <div className="upload-text">
                  <span className="upload-icon">📸</span>
                  <div>{t('admin.addMenuItem.clickToUpload')}</div>
                  <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.5rem' }}>{t('admin.addMenuItem.imageSpecs')}</div>
                </div>
              )}
            </div>
            <input type="file" id="item-image" accept="image/*" onChange={onImageChange} style={{ display: 'none' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary">✓ {t('admin.addMenuItem.saveButton', 'Save Item')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
